
import { useState } from 'react';

interface FAQItem {
  id: number;
  category: string;
  question: string;
  answer: string;
}

interface FAQCategory {
  id: string;
  label: string;
  icon: string;
}

interface FAQCategorySectionProps {
  category: FAQCategory;
  items: FAQItem[];
}

export default function FAQCategorySection({ category, items }: FAQCategorySectionProps) {
  const [collapsedItems, setCollapsedItems] = useState<number[]>([]);

  const isOpen = (id: number) => !collapsedItems.includes(id);

  const toggleItem = (id: number) => {
    setCollapsedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <section id={`faq-${category.id}`} className="scroll-mt-24">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-teal-100 text-teal-600">
          <i className={`${category.icon} text-xl`}></i>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{category.label}</h2>
          <p className="text-sm text-gray-500">{items.length} question{items.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((item) => {
          const open = isOpen(item.id);
          return (
            <div
              key={item.id}
              className={`bg-white rounded-xl border transition-all duration-300 ${
                open
                  ? 'border-teal-200 shadow-lg shadow-teal-50'
                  : 'border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200'
              }`}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <button
                onClick={() => toggleItem(item.id)}
                className="w-full flex items-center gap-4 p-5 text-left cursor-pointer group"
                aria-expanded={open}
              >
                <div className="flex-1 min-w-0">
                  <h3
                    className={`text-base font-semibold transition-colors duration-300 pr-4 ${
                      open ? 'text-teal-700' : 'text-gray-900 group-hover:text-teal-700'
                    }`}
                    itemProp="name"
                  >
                    {item.question}
                  </h3>
                </div>
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 transition-all duration-300 ${
                    open
                      ? 'bg-teal-600 text-white rotate-180'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-teal-100 group-hover:text-teal-600'
                  }`}
                >
                  <i className="ri-arrow-down-s-line text-lg"></i>
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-400 ease-in-out ${
                  open ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                }`}
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
              >
                <div className="px-5 pb-5">
                  <p className="text-gray-600 text-sm leading-relaxed" itemProp="text">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
